import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Package, Plus, Search, RefreshCw, Download, Edit, Trash2, Box, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TAGLIE = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'Unica'];

const emptyArticolo = {
  nome: '', categoria: 'Altro', descrizione: '', numero_seriale: '',
  quantita_totale: 1, taglia: '', note: ''
};

export default function MagazzinoInventario() {
  const [articoli, setArticoli] = useState([]);
  const [stats, setStats] = useState(null);
  const [categorie, setCategorie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('tutte');
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({...emptyArticolo});
  const [submitting, setSubmitting] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
  };

  const fetchAll = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filtroCategoria !== 'tutte') params.append('categoria', filtroCategoria);

      const [artRes, statsRes, catRes] = await Promise.all([
        fetch(`${API_URL}/api/magazzino/articoli?${params}`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/magazzino/stats`, { headers, credentials: 'include' }),
        fetch(`${API_URL}/api/magazzino/categorie`, { headers, credentials: 'include' })
      ]);

      if (artRes.ok) { const d = await artRes.json(); setArticoli(d.articoli || []); }
      if (statsRes.ok) { const d = await statsRes.json(); setStats(d); }
      if (catRes.ok) { const d = await catRes.json(); setCategorie(d.categorie || []); }
    } catch (error) {
      console.error('Errore:', error);
    } finally {
      setLoading(false);
    }
  }, [search, filtroCategoria]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSave = async () => {
    if (!formData.nome) { toast.error('Inserisci il nome dell\'articolo'); return; }
    setSubmitting(true);
    try {
      const headers = getAuthHeaders();
      const url = editingId
        ? `${API_URL}/api/magazzino/articoli/${editingId}`
        : `${API_URL}/api/magazzino/articoli`;
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, { method, headers, credentials: 'include', body: JSON.stringify(formData) });
      if (res.ok) {
        toast.success(editingId ? 'Articolo aggiornato' : 'Articolo aggiunto al magazzino');
        setShowDialog(false);
        setEditingId(null);
        setFormData({...emptyArticolo});
        fetchAll();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Errore nel salvataggio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (articolo_id, nome) => {
    if (!window.confirm(`Eliminare "${nome}" dal magazzino?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/magazzino/articoli/${articolo_id}`, {
        method: 'DELETE', headers: getAuthHeaders(), credentials: 'include'
      });
      if (res.ok) { toast.success('Articolo eliminato'); fetchAll(); }
      else { const err = await res.json(); toast.error(err.detail || 'Errore'); }
    } catch { toast.error('Errore di connessione'); }
  };

  const handleEdit = (art) => {
    setEditingId(art.articolo_id);
    setFormData({
      nome: art.nome, categoria: art.categoria, descrizione: art.descrizione || '',
      numero_seriale: art.numero_seriale || '', quantita_totale: art.quantita_totale,
      taglia: art.taglia || '', note: art.note || ''
    });
    setShowDialog(true);
  };

  const handleExportPDF = async () => {
    try {
      const res = await fetch(`${API_URL}/api/magazzino/inventario/pdf`, {
        headers: getAuthHeaders(), credentials: 'include'
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `inventario_magazzino_${new Date().toISOString().slice(0, 10)}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('PDF generato');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Errore nella generazione PDF');
      }
    } catch { toast.error('Errore di connessione'); }
  };

  const getStatoBadge = (art) => {
    if (art.quantita_disponibile <= 0) return <Badge className="bg-red-100 text-red-700">Esaurito</Badge>;
    if (art.quantita_disponibile < art.quantita_totale) return <Badge className="bg-amber-100 text-amber-700">Parziale</Badge>;
    return <Badge className="bg-green-100 text-green-700">Disponibile</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="magazzino-inventario">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Package className="w-6 h-6 text-indigo-600" />
            Inventario Magazzino
          </h1>
          <p className="text-sm text-slate-500 mt-1">Gestione articoli, materiali e dotazioni</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportPDF} data-testid="export-pdf-btn">
            <Download className="w-4 h-4 mr-2" /> PDF
          </Button>
          <Button onClick={() => { setEditingId(null); setFormData({...emptyArticolo}); setShowDialog(true); }} data-testid="nuovo-articolo-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuovo Articolo
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <Box className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totale_articoli}</p>
                <p className="text-xs text-slate-500">Articoli</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.quantita_totale}</p>
                <p className="text-xs text-slate-500">Pezzi Totali</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.quantita_disponibile}</p>
                <p className="text-xs text-slate-500">Disponibili</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.quantita_in_uso}</p>
                <p className="text-xs text-slate-500">In Uso</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtri */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Cerca articolo, seriale, categoria..." className="pl-10" value={search}
                onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchAll()} data-testid="search-input" />
            </div>
            <Select value={filtroCategoria} onValueChange={setFiltroCategoria}>
              <SelectTrigger className="w-[180px]" data-testid="filtro-categoria">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tutte">Tutte le categorie</SelectItem>
                {categorie.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchAll}><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabella */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Caricamento...</div>
          ) : articoli.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">Magazzino vuoto</p>
              <p className="text-sm mt-1">Aggiungi il primo articolo cliccando "Nuovo Articolo"</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Articolo</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead className="hidden md:table-cell">N. Seriale</TableHead>
                  <TableHead className="hidden md:table-cell">Taglia</TableHead>
                  <TableHead className="text-center">Totale</TableHead>
                  <TableHead className="text-center">In Uso</TableHead>
                  <TableHead className="text-center">Disp.</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {articoli.map((art) => (
                  <TableRow key={art.articolo_id} data-testid={`articolo-row-${art.articolo_id}`}>
                    <TableCell className="font-semibold">{art.nome}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{art.categoria}</Badge></TableCell>
                    <TableCell className="hidden md:table-cell font-mono text-sm">{art.numero_seriale || '-'}</TableCell>
                    <TableCell className="hidden md:table-cell">{art.taglia || '-'}</TableCell>
                    <TableCell className="text-center font-mono">{art.quantita_totale}</TableCell>
                    <TableCell className="text-center font-mono">{art.quantita_in_uso}</TableCell>
                    <TableCell className="text-center font-mono font-bold">{art.quantita_disponibile}</TableCell>
                    <TableCell>{getStatoBadge(art)}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(art)} data-testid={`edit-${art.articolo_id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700"
                        onClick={() => handleDelete(art.articolo_id, art.nome)} data-testid={`delete-${art.articolo_id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog Nuovo/Modifica */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifica Articolo' : 'Nuovo Articolo'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Nome Articolo *</Label>
              <Input value={formData.nome} onChange={(e) => setFormData({...formData, nome: e.target.value})}
                placeholder="Es: Radio Baofeng UV-5R" data-testid="input-nome" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Categoria</Label>
                <Select value={formData.categoria} onValueChange={(v) => setFormData({...formData, categoria: v})}>
                  <SelectTrigger data-testid="select-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categorie.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Quantita Totale</Label>
                <Input type="number" min="1" value={formData.quantita_totale}
                  onChange={(e) => setFormData({...formData, quantita_totale: parseInt(e.target.value) || 1})} data-testid="input-quantita" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>N. Seriale / Matricola</Label>
                <Input value={formData.numero_seriale} onChange={(e) => setFormData({...formData, numero_seriale: e.target.value})}
                  placeholder="Opzionale" data-testid="input-seriale" />
              </div>
              <div className="grid gap-2">
                <Label>Taglia (per divise)</Label>
                <Select value={formData.taglia || 'nessuna'} onValueChange={(v) => setFormData({...formData, taglia: v === 'nessuna' ? '' : v})}>
                  <SelectTrigger data-testid="select-taglia"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nessuna">Nessuna</SelectItem>
                    {TAGLIE.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Descrizione</Label>
              <Input value={formData.descrizione} onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                placeholder="Descrizione opzionale" data-testid="input-descrizione" />
            </div>
            <div className="grid gap-2">
              <Label>Note</Label>
              <Input value={formData.note} onChange={(e) => setFormData({...formData, note: e.target.value})}
                placeholder="Note interne" data-testid="input-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={submitting} data-testid="salva-btn">
              {submitting ? 'Salvataggio...' : (editingId ? 'Aggiorna' : 'Aggiungi')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
